import { Component, OnInit, ViewChild } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import {
    AuthenticationResult,
    EventMessage,
    EventType,
} from '@azure/msal-browser';
import { HelperService } from 'merlin-core';
import { SpinnerService, UserService, UserProfileObject } from 'merlin-library';
import { filter, take } from 'rxjs/operators';
import { LocalizationService } from 'src/core/utilitites/localization.service';
import { environment } from 'src/environments/environment';
import { HtmlSanitizerService } from 'src/core/utilitites/html-sanitizer.service';
import { GlobalInputSecurityService } from 'src/core/utilitites/global-input-security.service';
import { EditMfaRecoveryService } from './services/edit-mfa-recovery.service';
const pkg = require('package.json');
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit {
    title = 'merlin-cloud';
    frameBaseUrl: string;
    isOnOidcBase = true;

    /** Removes the global Save/Enroll XSS-validation listener on destroy. */
    private disposeGlobalInputSecurity: () => void;

    /**
     * Caches the last values written by `ngAfterViewChecked`. That hook runs
     * after EVERY change-detection cycle (i.e. on every keystroke), so we must
     * avoid re-sanitising / re-writing the DOM and localStorage when nothing
     * changed — doing so caused noticeable input lag.
     */
    private lastPaginatorLabel: string | null = null;
    private lastAllLabel: string | null = null;

    constructor(
        private authService: MsalService,
        private msalBroadcastService: MsalBroadcastService,
        private helperService: HelperService,
        public spinnerSer: SpinnerService,
        private meta: Meta,
        private localizationService: LocalizationService,
        private userService: UserService,
        private router: Router,
        private htmlSanitizer: HtmlSanitizerService,
        private globalInputSecurity: GlobalInputSecurityService,
        private editMfaRecovery: EditMfaRecoveryService
    ) {
        // Capture NTFYID parameter before routing happens
        const urlParams = new URLSearchParams(window.location.search);
        const ntfyId = urlParams.get('NTFYID');

        if (ntfyId) {
            try {
                // Decode base64 → "WebsiteNotificationID|PatientID" or "WebsiteNotificationID|PatientID|ReportTypeCode"
                const decoded = atob(ntfyId);
                const parts = decoded.split('|');
                const isValidId = (s: string) => /^\d+$/.test(s);
                if (parts.length >= 2 && isValidId(parts[0]) && isValidId(parts[1])) {
                    window.sessionStorage.setItem('emailWebsiteNotificationId', parts[0]);
                    window.sessionStorage.setItem('emailPatientId', parts[1]);
                    if (parts[2] && isValidId(parts[2])) {
                        // Numeric 3rd part = LocationId
                        window.sessionStorage.setItem('emailLocationId', parts[2]);
                        window.sessionStorage.removeItem('emailReportTypeCode');
                    } else if (parts[2]) {
                        // Alpha 3rd part = ReportTypeCode
                        window.sessionStorage.setItem('emailReportTypeCode', parts[2]);
                        window.sessionStorage.removeItem('emailLocationId');
                    } else {
                        window.sessionStorage.removeItem('emailReportTypeCode');
                        window.sessionStorage.removeItem('emailLocationId');
                    }
                    window.sessionStorage.removeItem('emailLinkInvalid');
                } else {
                    window.sessionStorage.removeItem('emailWebsiteNotificationId');
                    window.sessionStorage.removeItem('emailPatientId');
                    window.sessionStorage.removeItem('emailReportTypeCode');
                    window.sessionStorage.setItem('emailLinkInvalid', 'true');
                }
            } catch (error) {
                // Invalid base64 — invalid NTFYID
                window.sessionStorage.removeItem('emailLocationId');
                window.sessionStorage.setItem('emailLinkInvalid', 'true');
            }
        }

        this.msalBroadcastService.msalSubject$
            .pipe(
                filter(
                    (msg: EventMessage) => msg.eventType === EventType.LOGIN_FAILURE
                )
            )
            .subscribe(() => {
                if (this.editMfaRecovery.isEditMfaReturn()) {
                    this.editMfaRecovery.recoverToRemoteProgramming();
                } else {
                    sessionStorage.removeItem('firstTimeMsalEditBtn');
                }
            });

        this.msalBroadcastService.msalSubject$
            .pipe(
                filter(
                    (msg: EventMessage) =>
                        msg.eventType === EventType.LOGIN_SUCCESS
                )
            )
            .subscribe((result: EventMessage) => {
                const payload = result.payload as AuthenticationResult;
                const isRemoteProgEditMfa = payload.authority
                    ?.toLowerCase().includes(environment.remoteProgEditMFA.toLowerCase());
                if (!isRemoteProgEditMfa) {
                    window.sessionStorage.setItem(
                        'activeAccount',
                        JSON.stringify(payload.account)
                    );
                    this.authService.instance.setActiveAccount(payload.account);
                    sessionStorage.removeItem('firstTimeMsalEditBtn');
                } else {
                    // Edit-MFA login succeeded; mark satisfied using the stored patientId from setFlags.
                    this.editMfaRecovery.markMfaSatisfied();
                    // Restore primary account so MsalInterceptor does not loop on edit-MFA authority.
                    this.editMfaRecovery.restorePrimaryAccount();                }
            });
    }
    ngOnInit(): void {
        // Register application-wide XSS / SQL-Injection validation. This single
        // capture-phase listener covers EVERY <input> / <textarea> in the whole
        // app: typing is never blocked, but pressing any Save / Enroll button
        // validates the fields, highlights offenders and shows one toaster.
        this.disposeGlobalInputSecurity = this.globalInputSecurity.register();

        if (environment.production) {
            this.frameBaseUrl = `${environment.baseUrl}/merlin-cloud/assets/pdfjs/web/viewer.html`;
        } else {
            this.frameBaseUrl = `${environment.baseUrl}assets/pdfjs/web/viewer.html`;
        }
        this.meta.updateTag({
            'http-equiv': 'Content-Security-Policy',
            content: `frame-src ${this.frameBaseUrl} ${environment.disclosureUrl}`
        });
        if (!environment.production) {
            // For Dev
            this.meta.addTag({
                name:'merlin-cloud',
                'data-appVer': pkg.version,
                'data-angFwVer': pkg.devDependencies['@angular/cli'],
                'data-merlinLibraryVer': pkg.dependencies['merlin-library'].replace('file:merlin-library-', '').replace('.tgz', '')
            })
        }

        // Send clinic name to New Relic on each web page
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd)
        ).subscribe((event: NavigationEnd) => {
                this.isOnOidcBase = event.urlAfterRedirects.startsWith('/base');
                this.userService.getUserObs().pipe(take(1)).subscribe((userProfile: UserProfileObject) => {
                const clinicName = (userProfile as any)?.customerName;
                const newrelic = typeof window !== 'undefined' ? (window as any).newrelic : null;

                if (userProfile && clinicName && newrelic) {
                    newrelic.setCustomAttribute('clinicName', clinicName);
                }
            });
        });
    }
    ngAfterViewChecked() {
        const dom = document.getElementsByClassName(
            'mat-paginator-page-size-label'
        );
        if (dom[0]) {
            const txs = this.translate(
                'label.gb.items_per_page'
            );
            // Only sanitise + write to the live DOM when the label actually
            // changed. This hook fires on every change-detection cycle (every
            // keystroke), so guarding here removes per-keystroke lag while the
            // paginator label can still never become an XSS sink.
            if (txs !== this.lastPaginatorLabel) {
                this.lastPaginatorLabel = txs;
                dom[0].innerHTML = this.htmlSanitizer.sanitize(txs);
            }
        }
        const allLabel = this.translate('label.filter.all');
        if (allLabel !== this.lastAllLabel) {
            this.lastAllLabel = allLabel;
            localStorage.setItem('allLabel', allLabel);
        }
    }
    translate(key: string) {
        return this.localizationService.generateLocalizedStr(key);
    }

    ngOnDestroy(): void {
        this.disposeGlobalInputSecurity?.();
        this.helperService._destroying$.next(undefined);
        this.helperService._destroying$.complete();
    }
}
